/**
 * Live ✓/✗ checklist under a password field — shows what is still missing
 * while typing rather than only on submit. The rules themselves live in
 * `@/lib/passwordPolicy` (shared with every form's validation).
 */
import { CheckCircleFilled, CloseCircleOutlined } from "@ant-design/icons";
import { Space, Typography, theme } from "antd";

import {
  PASSWORD_MIN_CLASSES,
  PASSWORD_MIN_LENGTH,
  countCharacterClasses,
} from "@/lib/passwordPolicy";

interface PasswordChecklistProps {
  /** Current field value; `undefined` before the user types anything. */
  value?: string;
}

export function PasswordChecklist({ value }: PasswordChecklistProps) {
  const { token } = theme.useToken();
  const password = value ?? "";
  const checks = [
    {
      label: `At least ${PASSWORD_MIN_LENGTH} characters`,
      met: password.length >= PASSWORD_MIN_LENGTH,
    },
    {
      label: "Three of: lowercase, uppercase, number, symbol",
      met: countCharacterClasses(password) >= PASSWORD_MIN_CLASSES,
    },
  ];

  return (
    <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
      {checks.map((check) => (
        <Typography.Text
          key={check.label}
          style={{
            fontSize: 12,
            color: check.met ? token.colorSuccess : token.colorTextTertiary,
          }}
        >
          {check.met ? (
            <CheckCircleFilled style={{ marginRight: 6 }} />
          ) : (
            <CloseCircleOutlined style={{ marginRight: 6 }} />
          )}
          {check.label}
        </Typography.Text>
      ))}
    </Space>
  );
}

export default PasswordChecklist;
